import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { experiences } from '../data';
import CategoryTabs from '../components/CategoryTabs';
import PackageSection from '../components/PackageSection';

const PRICE_RANK = {
  $: 1,
  $$: 2,
  $$$: 3,
};

const normalizeValue = (value) =>
  String(value || '').trim().toLowerCase();

const parseGroupRange = (value) => {
  const text = String(value || '');
  const numbers = text.match(/\d+/g)?.map(Number) || [];

  if (text.includes('+') && numbers.length > 0) {
    return [numbers[0], Infinity];
  }

  if (numbers.length >= 2) {
    return [numbers[0], numbers[1]];
  }

  if (numbers.length === 1) {
    return [numbers[0], numbers[0]];
  }

  return [0, Infinity];
};

const rangesOverlap = ([minimumA, maximumA], [minimumB, maximumB]) =>
  minimumA <= maximumB && minimumB <= maximumA;

const getExperienceCategories = (experience) => {
  const categories =
    experience.primaryCategories ??
    (experience.primaryCategory
      ? [experience.primaryCategory]
      : ['outings']);

  return (Array.isArray(categories) ? categories : [categories]).map(
    normalizeValue
  );
};

const getExperienceVibes = (experience) => {
  const vibes = Array.isArray(experience.vibe)
    ? experience.vibe
    : [experience.vibe];

  return vibes.filter(Boolean).map(normalizeValue);
};

const getExperienceSettings = (experience) => {
  const settings = Array.isArray(experience.setting)
    ? experience.setting
    : [experience.setting];

  return settings.filter(Boolean);
};

const plannerDefaults = {
  city: 'Denver',
  groupSize: '11–25',
  budget: '$$',
  setting: 'Any',
  vibe: 'Any',
};

const categoryFilterConfig = {
  outings: {
    label: 'Vibe',
    options: [
      'Any',
      'Active',
      'Creative',
      'Competitive',
      'Social',
      'Relaxed',
      'Purpose-driven',
    ],
  },

  trainings: {
    label: 'Training Focus',
    options: [
      'Any',
      'Leadership',
      'Communication',
      'Team Building',
      'Professional Development',
      'Sales',
      'Workplace Wellness',
    ],
  },

  speakers: {
    label: 'Speaking Topic',
    options: [
      'Any',
      'Leadership',
      'Motivation',
      'Communication',
      'Workplace Culture',
      'Wellness',
      'Innovation',
    ],
  },

  retreats: {
    label: 'Retreat Goal',
    options: [
      'Any',
      'Team Bonding',
      'Strategic Planning',
      'Leadership Development',
      'Wellness',
      'Celebration',
      'Rest and Recharge',
    ],
  },

  volunteer: {
    label: 'Cause Area',
    options: [
      'Any',
      'Community',
      'Hunger Relief',
      'Environment',
      'Youth',
      'Animals',
      'Housing',
    ],
  },
};

const HomePage = () => {
  const [planner, setPlanner] = useState(plannerDefaults);
  const [activeCategory, setActiveCategory] = useState('outings');

  const categoryFilter = categoryFilterConfig[activeCategory];

  const handleCategoryChange = (categoryId) => {
    setActiveCategory(categoryId);

    setPlanner((current) => ({
      ...current,
      vibe: 'Any',
      budget: categoryId === 'outings' ? '$$' : '$$$',
    }));
  };

  const plannerResults = useMemo(() => {
    const selectedGroupRange = parseGroupRange(planner.groupSize);
    const selectedBudgetRank = PRICE_RANK[planner.budget] || Infinity;

    const rankedExperiences = experiences.map((experience) => {
      const experienceCategories =
        getExperienceCategories(experience);

      const experienceVibes = getExperienceVibes(experience);
      const experienceSettings =
        getExperienceSettings(experience);

      const categoryMatch = experienceCategories.includes(
        normalizeValue(activeCategory)
      );

      const cityMatch =
        normalizeValue(experience.city) ===
        normalizeValue(planner.city);

      const groupMatch = rangesOverlap(
        parseGroupRange(experience.groupSize),
        selectedGroupRange
      );

      const experienceBudgetRank =
        PRICE_RANK[experience.price] || Infinity;

      const budgetMatch =
        experienceBudgetRank <= selectedBudgetRank;

      const settingMatch =
        planner.setting === 'Any' ||
        experienceSettings.some((setting) =>
          normalizeValue(setting).includes(
            normalizeValue(planner.setting)
          )
        );

      const vibeMatch =
        planner.vibe === 'Any' ||
        experienceVibes.includes(normalizeValue(planner.vibe));

      const differences = [];

      if (!cityMatch) {
        differences.push(
          `Different city: ${experience.city}, ${experience.state}`
        );
      }

      if (!groupMatch) {
        differences.push(
          `Group-size range: ${experience.groupSize} people`
        );
      }

      if (!budgetMatch) {
        differences.push(
          `Above selected budget: ${experience.price}`
        );
      }

      if (!settingMatch) {
        differences.push(
          `Different setting: ${experienceSettings.join(', ')}`
        );
      }

      if (!vibeMatch) {
        const displayedVibes = Array.isArray(experience.vibe)
          ? experience.vibe.join(', ')
          : experience.vibe;

        differences.push(
          `Different ${categoryFilter.label.toLowerCase()}: ${
            displayedVibes || 'not specified'
          }`
        );
      }

      if (!categoryMatch) {
        differences.push(
          `Different experience type: ${experience.category}`
        );
      }

      const isExactMatch =
        categoryMatch &&
        cityMatch &&
        groupMatch &&
        budgetMatch &&
        settingMatch &&
        vibeMatch;

      let score = 0;

      if (categoryMatch) score += 100;
      if (cityMatch) score += 40;
      if (groupMatch) score += 35;
      if (budgetMatch) score += 20;
      if (settingMatch) score += 10;
      if (vibeMatch) score += 8;

      if (experience.price === planner.budget) {
        score += 3;
      }

      return {
        experience,
        differences,
        isExactMatch,
        categoryMatch,
        score,
      };
    });

    const sortByCloseness = (first, second) =>
      second.score - first.score ||
      first.differences.length - second.differences.length;

    const exactMatches = rankedExperiences
      .filter((item) => item.isExactMatch)
      .sort(sortByCloseness)
      .slice(0, 3);

    const sameCategoryAlternatives = rankedExperiences
      .filter(
        (item) =>
          !item.isExactMatch &&
          item.categoryMatch
      )
      .sort(sortByCloseness);

    const otherCategoryAlternatives = rankedExperiences
      .filter(
        (item) =>
          !item.isExactMatch &&
          !item.categoryMatch
      )
      .sort(sortByCloseness);

    const closestMatches = [
      ...sameCategoryAlternatives,
      ...otherCategoryAlternatives,
    ].slice(0, 3);

    return {
      exactMatches,
      closestMatches,
    };
  }, [
    activeCategory,
    categoryFilter.label,
    planner,
  ]);

  const plannerMatches = plannerResults.exactMatches;
  const closestMatches = plannerResults.closestMatches;
  const hasExactMatches = plannerMatches.length > 0;

  const updatePlanner = (field, value) => {
    setPlanner((current) => ({
      ...current,
      [field]: value,
    }));
  };

  return (
    <main>
      <section className="hero">
        <div className="hero-content">
          <p className="eyebrow">
            AI-powered team outing planning
          </p>

          <h1>Outdo the Conference Room.</h1>

          <p className="hero-copy">
            Office Outing helps busy managers, HR teams, and
            executive assistants find the right team experience
            in minutes — not hours.
          </p>

          <div className="hero-actions">
            <button
              type="button"
              className="button primary"
              onClick={() => {
                const plannerSection =
                  document.getElementById('planner');

                if (!plannerSection) {
                  return;
                }

                const headerOffset = 115;
                const plannerPosition =
                  plannerSection.getBoundingClientRect().top +
                  window.scrollY;

                window.scrollTo({
                  top: plannerPosition - headerOffset,
                  behavior: 'smooth',
                });
              }}
            >
              Find the Right Experience
            </button>

            <Link
              to="/browse"
              className="button secondary"
            >
              Browse All Experiences
            </Link>
          </div>
                          <div
            className="startup-award-badge"
            aria-label="2023 Casper Start-Up Challenge Winner"
          >
            <span
              className="startup-award-icon"
              aria-hidden="true"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M8 4h8v4.5a4 4 0 0 1-8 0V4Z"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M8 6H5.5v1.5A3.5 3.5 0 0 0 9 11"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M16 6h2.5v1.5A3.5 3.5 0 0 1 15 11"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M12 12.5V17"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  strokeLinecap="round"
                />
                <path
                  d="M9 20h6"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  strokeLinecap="round"
                />
                <path
                  d="M10 17h4"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  strokeLinecap="round"
                />
              </svg>
            </span>

            <span>
              2023 Casper Start-Up Challenge Winner
            </span>
          </div>
        </div>

        <div className="hero-card">
          <span>Launch cities</span>
          <strong>Casper + Denver</strong>

          <p>
            Curated ideas for real teams, real budgets, and real
            schedules.
          </p>
        </div>
      </section>

      <section className="value-section">
        <div>
          <span className="step-number">01</span>
          <h3>Tell us the basics</h3>

          <p>
            Choose your city, group size, budget, setting, and
            style.
          </p>
        </div>

        <div>
          <span className="step-number">02</span>
          <h3>Browse instant matches</h3>

          <p>
            See curated options that fit your team instead of
            scrolling endless lists.
          </p>
        </div>

        <div>
          <span className="step-number">03</span>
          <h3>Get planning support</h3>

          <p>
            Request a custom shortlist or planning assistance when
            you want help narrowing it down.
          </p>
        </div>
      </section>

      <section
        id="planner"
        className="planner-section"
      >
        <div className="section-heading">
          <p className="eyebrow">
            Office Outing Planner
          </p>

          <h2>
            Find a strong starting point for your team.
          </h2>

          <p>
            Answer a few questions and browse options for your
            city, group size, budget, setting, and goals.
          </p>
        </div>

        <CategoryTabs
          activeCategory={activeCategory}
          onCategoryChange={handleCategoryChange}
        />

        <div className="personalized-ideas-link">
          <span aria-hidden="true">✨</span>
          <span>Not sure what you need?</span>

          <Link to="/personalized-ideas">
            Get 3 personalized ideas →
          </Link>
        </div>

        <div className="planner-grid">
          <div className="planner-form">
            <div className="planner-field">
              <label htmlFor="planner-city">
                City
              </label>

              <select
                id="planner-city"
                value={planner.city}
                onChange={(event) =>
                  updatePlanner('city', event.target.value)
                }
              >
                <option value="Denver">
                  Denver, CO
                </option>

                <option value="Casper">
                  Casper, WY
                </option>
              </select>
            </div>

            <div className="planner-field">
              <label htmlFor="planner-group-size">
                Group size
              </label>

              <select
                id="planner-group-size"
                value={planner.groupSize}
                onChange={(event) =>
                  updatePlanner(
                    'groupSize',
                    event.target.value
                  )
                }
              >
                <option>5–10</option>
                <option>11–25</option>
                <option>26–50</option>
                <option>50+</option>
              </select>
            </div>

            <div className="planner-field">
              <label htmlFor="planner-budget">
                Budget
              </label>

              <select
                id="planner-budget"
                value={planner.budget}
                onChange={(event) =>
                  updatePlanner('budget', event.target.value)
                }
              >
                <option>$</option>
                <option>$$</option>
                <option>$$$</option>
              </select>
            </div>

            <div className="planner-field">
              <label htmlFor="planner-setting">
                Setting
              </label>

              <select
                id="planner-setting"
                value={planner.setting}
                onChange={(event) =>
                  updatePlanner('setting', event.target.value)
                }
              >
                <option>Any</option>
                <option>Indoor</option>
                <option>Outdoor</option>
                <option>Virtual</option>
              </select>
            </div>

            <div className="planner-field">
              <label htmlFor="planner-category-filter">
                {categoryFilter.label}
              </label>

              <select
                id="planner-category-filter"
                value={planner.vibe}
                onChange={(event) =>
                  updatePlanner('vibe', event.target.value)
                }
              >
                {categoryFilter.options.map((option) => (
                  <option
                    key={option}
                    value={option}
                  >
                    {option}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div
            className="planner-results"
            aria-live="polite"
          >
            {hasExactMatches ? (
              <>
                <h3>Top matches</h3>

                {plannerMatches.map(({ experience }) => (
                  <Link
                    to={`/business/${experience.id}`}
                    className="mini-result"
                    key={experience.id}
                  >
                    <img
                      src={experience.image}
                      alt={experience.name}
                    />

                    <div>
                      <strong>
                        {experience.name}
                      </strong>

                      <span>
                        {experience.city},{' '}
                        {experience.state} ·{' '}
                        {experience.price} ·{' '}
                        {experience.duration}
                      </span>

                      <p>
                        {experience.summary}
                      </p>
                    </div>
                  </Link>
                ))}
              </>
            ) : (
              <>
                <div className="no-exact-matches">
                  <h3>
                    No exact matches for these filters.
                  </h3>

                  <p>
                    Try changing one filter, or explore the
                    closest available options below.
                  </p>
                </div>

                {closestMatches.length > 0 && (
                  <div className="closest-options">
                    <h3>
                      Closest available options
                    </h3>

                    {closestMatches.map(
                      ({ experience, differences }) => (
                        <Link
                          to={`/business/${experience.id}`}
                          className="mini-result closest-result"
                          key={experience.id}
                        >
                          <img
                            src={experience.image}
                            alt={experience.name}
                          />

                          <div className="closest-result-copy">
                            <strong>
                              {experience.name}
                            </strong>

                            <span>
                              {experience.city},{' '}
                              {experience.state} ·{' '}
                              {experience.price} ·{' '}
                              {experience.duration}
                            </span>

                            <p>
                              {experience.summary}
                            </p>

                            <div className="closest-result-differences">
                              <strong>
                                What’s different:
                              </strong>

                              <div className="closest-difference-list">
                                {differences.map(
                                  (difference) => (
                                    <span
                                      key={`${experience.id}-${difference}`}
                                    >
                                      {difference}
                                    </span>
                                  )
                                )}
                              </div>
                            </div>
                          </div>
                        </Link>
                      )
                    )}
                  </div>
                )}
              </>
            )}

            <div className="custom-results-cta">
              <div className="cta-badge">
                Free personalized review
              </div>

              <div className="cta-icon">
                ✨
              </div>

              <h3>
                {hasExactMatches
                  ? 'Want a more tailored shortlist?'
                  : 'Still not finding the right fit?'}
              </h3>

              {hasExactMatches ? (
                <p>
                  These are instant matches based on your filters.
                  Tell us a little more about your team, and we’ll
                  send you{' '}
                  <strong>
                    3 personalized experience ideas
                  </strong>{' '}
                  matched to your budget, group size, goals, and
                  timing.
                </p>
              ) : (
                <p>
                  Tell us a little more about your team, and we’ll
                  send you{' '}
                  <strong>
                    3 personalized experience ideas
                  </strong>{' '}
                  based on your budget, group size, goals, and
                  timing.
                </p>
              )}

              <div className="custom-shortlist-action">
                <Link
                  to="/personalized-ideas"
                  className="custom-shortlist-button"
                >
                  Get My Custom Shortlist
                </Link>
              </div>

              <div className="cta-proof">
                <span>✓ Free</span>
                <span>
                  ✓ Reviewed by a real person
                </span>
                <span>✓ Delivered by email</span>
              </div>

              <p className="pricing-note">
                Browsing experience ideas and requesting a custom
                shortlist is free. Optional coordination support
                may be available for teams that want more hands-on
                help.
              </p>
            </div>
          </div>
        </div>
      </section>

      <PackageSection />

      <div className="home-browse-link">
        <p>
          Prefer to explore options on your own?
        </p>

        <Link
          to="/browse"
          className="home-browse-button"
        >
          Browse all options
          <span aria-hidden="true">→</span>
        </Link>
      </div>
    </main>
  );
};

export default HomePage;
